import { useState } from 'react';
import BookCallCTA from '../components/BookCallCTA';
import { useSeo } from '../hooks/useSeo';

export default function ROICalculatorPage() {
  const [monthlyLeads, setMonthlyLeads] = useState(100);
  const [currentContactRate, setCurrentContactRate] = useState(30);
  const [currentBookingRate, setCurrentBookingRate] = useState(15);
  const [avgCommission, setAvgCommission] = useState(8000);
  const [closingRate, setClosingRate] = useState(20);

  useSeo({
    title: 'AI Caller ROI Calculator | Point Break Systems',
    description: 'Calculate your projected ROI from deploying an AI voice caller. See how many more appointments and commissions you can generate from your existing lead volume.',
    path: '/roi-calculator',
    schemas: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "AI Caller ROI Calculator",
        "description": "Calculate your projected ROI from AI voice callers for real estate lead conversion",
        "url": "https://pointbreak.systems/roi-calculator",
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://pointbreak.systems" },
            { "@type": "ListItem", "position": 2, "name": "ROI Calculator", "item": "https://pointbreak.systems/roi-calculator" }
          ]
        }
      },
    ],
  });

  const aiContactRate = Math.min(currentContactRate * 2.2, 85);
  const aiBookingRate = Math.min(currentBookingRate * 1.8, 65);

  const currentContactsPerMonth = Math.round(monthlyLeads * (currentContactRate / 100));
  const currentAppointmentsPerMonth = Math.round(currentContactsPerMonth * (currentBookingRate / 100));
  const currentDealsPerMonth = Math.round(currentAppointmentsPerMonth * (closingRate / 100));
  const currentAnnualRevenue = currentDealsPerMonth * avgCommission * 12;

  const aiContactsPerMonth = Math.round(monthlyLeads * (aiContactRate / 100));
  const aiAppointmentsPerMonth = Math.round(aiContactsPerMonth * (aiBookingRate / 100));
  const aiDealsPerMonth = Math.round(aiAppointmentsPerMonth * (closingRate / 100));
  const aiAnnualRevenue = aiDealsPerMonth * avgCommission * 12;

  const additionalRevenue = aiAnnualRevenue - currentAnnualRevenue;
  const additionalAppointments = aiAppointmentsPerMonth - currentAppointmentsPerMonth;

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(n);

  const sliders = [
    {
      label: "Monthly leads received",
      value: monthlyLeads,
      min: 10,
      max: 1000,
      step: 10,
      onChange: setMonthlyLeads,
      display: `${monthlyLeads} leads/mo`,
      tooltip: "Total new leads entering your pipeline each month from all sources"
    },
    {
      label: "Current contact rate",
      value: currentContactRate,
      min: 5,
      max: 80,
      step: 5,
      onChange: setCurrentContactRate,
      display: `${currentContactRate}%`,
      tooltip: "Percentage of leads you actually reach by phone today"
    },
    {
      label: "Appointment booking rate (of contacts)",
      value: currentBookingRate,
      min: 5,
      max: 60,
      step: 5,
      onChange: setCurrentBookingRate,
      display: `${currentBookingRate}%`,
      tooltip: "Of the leads you reach, how many book a showing or consultation"
    },
    {
      label: "Average commission per closed deal",
      value: avgCommission,
      min: 3000,
      max: 50000,
      step: 1000,
      onChange: setAvgCommission,
      display: formatCurrency(avgCommission),
      tooltip: "Your average gross commission on a closed transaction"
    },
    {
      label: "Closing rate (appointments to deals)",
      value: closingRate,
      min: 5,
      max: 60,
      step: 5,
      onChange: setClosingRate,
      display: `${closingRate}%`,
      tooltip: "Percentage of booked appointments that result in a closed transaction"
    },
  ];

  const metrics = [
    { label: 'Leads contacted/mo', current: currentContactsPerMonth, ai: aiContactsPerMonth, isNumber: true },
    { label: 'Appointments/mo', current: currentAppointmentsPerMonth, ai: aiAppointmentsPerMonth, isNumber: true },
    { label: 'Deals/mo', current: currentDealsPerMonth, ai: aiDealsPerMonth, isNumber: true },
    { label: 'Annual revenue', current: currentAnnualRevenue, ai: aiAnnualRevenue, isNumber: false },
  ];

  return (
    <div className="bg-white min-h-screen"><div className="max-w-5xl mx-auto px-5 sm:px-6 py-8 sm:py-14">
      <header className="mb-6"><h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">ROI calculator</h1><p className="text-base text-gray-600">Adjust your numbers to explore a calling scenario. Estimates are in CAD, before system costs.</p></header>
      <div className="lg:hidden sticky top-[calc(4rem+env(safe-area-inset-top))] z-20 bg-white border-y border-gray-200 py-3 mb-5 flex flex-wrap items-center justify-between gap-2"><span className="text-sm text-gray-600">Estimated annual gain</span><output className="text-xl font-bold text-gray-900">{formatCurrency(additionalRevenue)}</output></div>
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
        <section aria-labelledby="calculator-inputs" className="min-w-0">
          <h2 id="calculator-inputs" className="text-xl font-bold text-gray-900 mb-5">Your current numbers</h2>
          <div className="space-y-5">{sliders.map(({ label, value, min, max, step, onChange, display, tooltip }, i) => <div key={label}>
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1"><label htmlFor={`roi-input-${i}`} className="text-sm font-semibold text-gray-800">{label}</label><output htmlFor={`roi-input-${i}`} className="text-base font-bold text-gray-900">{display}</output></div>
            <input id={`roi-input-${i}`} type="range" min={min} max={max} step={step} value={value} aria-describedby={`roi-help-${i}`} onChange={e => onChange(Number(e.target.value))} className="w-full h-8 accent-[#d4a10d] cursor-pointer" />
            <p id={`roi-help-${i}`} className="text-sm text-gray-500">{tooltip}</p>
          </div>)}</div>
        </section>
        <section aria-labelledby="calculator-results" className="min-w-0 bg-[#fdfcf9] rounded-lg p-5 sm:p-6">
          <h2 id="calculator-results" className="text-sm font-semibold text-gray-600 mb-2">Estimated additional annual commission</h2>
          <p className="text-3xl sm:text-4xl font-bold text-gray-900 break-words">{formatCurrency(additionalRevenue)}</p>
          <p className="text-base text-gray-600 mt-2 mb-5">{additionalAppointments >= 0 ? '+' : ''}{additionalAppointments} appointments per month in this scenario</p>
          <table className="w-full text-sm text-left"><caption className="sr-only">Current results compared with the AI calling scenario</caption><thead><tr className="border-b border-gray-300"><th scope="col" className="py-3 font-medium text-gray-600">Metric</th><th scope="col" className="py-3 text-right font-medium text-gray-600">Current</th><th scope="col" className="py-3 text-right font-medium text-gray-600">Scenario</th></tr></thead><tbody>{metrics.map(({label, current, ai, isNumber}) => <tr key={label} className="border-b border-gray-200"><th scope="row" className="py-3 pr-2 font-medium text-gray-700">{label}</th><td className="py-3 text-right text-gray-700">{isNumber ? current.toLocaleString() : formatCurrency(current)}</td><td className="py-3 pl-2 text-right font-semibold text-gray-900">{isNumber ? ai.toLocaleString() : formatCurrency(ai)}</td></tr>)}</tbody></table>
          <p className="text-sm text-gray-600 leading-relaxed mt-4 mb-5">Illustrative scenario, not a forecast. Results depend on lead quality, your market, and follow-up.</p>
          <BookCallCTA />
        </section>
      </div>
      <details className="mt-8 border-y border-gray-200 group"><summary className="py-4 cursor-pointer font-semibold text-base text-gray-900">How this estimate works</summary><p className="text-base text-gray-600 leading-relaxed pb-5 max-w-3xl">The model multiplies your contact rate by 2.2 (capped at 85%) and booking rate by 1.8 (capped at 65%). These are modelling assumptions, not verified averages. Monthly contacts, appointments, and deals are rounded to whole numbers. Annual commission is monthly deals × commission × 12. Setup, retainer, and platform costs are excluded.</p></details>
    </div></div>
  );
}
